import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, AlertTriangle, CheckCircle, TrendingUp, TrendingDown, Minus,
  Shield, ChevronRight, Download
} from 'lucide-react';

import { 
  calculateAllDomainScores, 
  calculateAllGroupScores, 
  calculateEnhancedScores,
  getDomainMaturityBand,
  SECTION_GROUPS
} from '@/lib/scoring-engine';
import { SECTION_CONFIG } from '@/config/domain-weights';
import { SECTION_ORDER } from '@/config/question-config';
import { exportComparisonPDF } from '@/lib/pdf-generator';

const RISK_COLORS = {
  CRITICAL: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', bar: 'bg-red-500' },
  Critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', bar: 'bg-red-500' },
  HIGH: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', bar: 'bg-orange-500' },
  High: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', bar: 'bg-orange-500' },
  MODERATE: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', bar: 'bg-amber-500' },
  Medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', bar: 'bg-amber-500' },
  LOW: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', bar: 'bg-emerald-500' },
  Low: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', bar: 'bg-emerald-500' },
};

const ScoreBar = ({ score, maxScore = 100, color = 'bg-slate-600' }) => (
  <div className="w-full bg-slate-100 rounded-full h-2.5">
    <div 
      className={`h-2.5 rounded-full transition-all ${color}`} 
      style={{ width: `${Math.min(100, (score / maxScore) * 100)}%` }}
    />
  </div>
);

const DeltaIndicator = ({ current, previous }) => {
  if (previous === null || previous === undefined || current === null || current === undefined) return null;
  const delta = current - previous;
  if (delta === 0) return <span className="text-xs text-slate-400 flex items-center gap-0.5"><Minus className="h-3 w-3" /> 0</span>;
  if (delta > 0) return <span className="text-xs text-emerald-600 flex items-center gap-0.5"><TrendingUp className="h-3 w-3" /> +{delta}%</span>;
  return <span className="text-xs text-red-600 flex items-center gap-0.5"><TrendingDown className="h-3 w-3" /> {delta}%</span>;
};

const computeScores = (answers) => {
  if (!answers || Object.keys(answers).length === 0) {
    return { finalScore: 0, groupScores: { CES: 0, PGS: 0, CMS: 0 }, domainScores: {}, auditRisk: 'Critical', band: 'Critical', reasons: [] };
  }
  
  const enhanced = calculateEnhancedScores(answers);
  const domainScores = calculateAllDomainScores(answers);
  const groupScores = calculateAllGroupScores(domainScores);

  return {
    finalScore: enhanced.finalScore,
    groupScores,
    domainScores,
    auditRisk: enhanced.auditFailureLikelihood,
    band: enhanced.scoreBand?.label || 'N/A',
    reasons: enhanced.auditFailureReasons || [],
  };
};

const ComparisonView = ({ assessmentIds, onBack }) => {
  const { authFetch } = useAuth();
  const [assessments, setAssessments] = useState([null, null]);
  const [scores, setScores] = useState([null, null]);
  const [loading, setLoading] = useState(true);

  const safeParse = async (res) => {
    try { const t = await res.text(); return JSON.parse(t); } catch { return null; }
  };

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        assessmentIds.map(async (id) => {
          const res = await authFetch(`/api/assessments/${id}`);
          return res.ok ? await safeParse(res) : null;
        })
      );
      setAssessments(results);
      setScores(results.map(a => a ? computeScores(a.answers) : null));
    } catch (err) {
      console.error('Failed to load assessments:', err);
    } finally {
      setLoading(false);
    }
  }, [assessmentIds, authFetch]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto"></div>
          <p className="text-slate-400 mt-4 text-sm">Loading comparison...</p>
        </div>
      </div>
    );
  }

  const [a1, a2] = assessments;
  const [s1, s2] = scores;

  if (!a1 || !a2) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <Button variant="outline" onClick={onBack} className="gap-2 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </Button>
        <p className="text-slate-500">Failed to load one or both assessments.</p>
      </div>
    );
  }

  const domainSections = SECTION_ORDER.filter(id => id !== 'engagement');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="comparison-view">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-2" data-testid="comparison-back-btn">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Button>
          <div className="h-6 w-px bg-slate-200" />
          <h1 className="text-base font-semibold text-slate-900 flex-1">Assessment Comparison</h1>
          {s1 && s2 && (
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={() => exportComparisonPDF(a1, a2, s1, s2)}
              data-testid="export-comparison-pdf"
            >
              <Download className="h-4 w-4" />
              Export PDF
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Client names header */}
        <div className="grid grid-cols-2 gap-6">
          {[a1, a2].map((a, i) => (
            <Card key={a.id} className={`border-t-4 ${i === 0 ? 'border-t-blue-500' : 'border-t-emerald-500'}`}>
              <CardContent className="p-5">
                <h2 className="text-lg font-bold text-slate-900">{a.client_name}</h2>
                <p className="text-sm text-slate-500 mt-1">Assessor: {a.user_name}</p>
                <div className="text-xs text-slate-400">
                  {new Date(a.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {' '}&middot;{' '}
                  <Badge variant="outline" className="text-xs">
                    {a.status.replace('_', ' ')}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Overall scores comparison */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Overall Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-8">
              {[s1, s2].map((s, i) => {
                const riskConf = RISK_COLORS[s?.auditRisk] || RISK_COLORS.Critical;
                return (
                  <div key={i} className="space-y-4">
                    <div className="flex items-end gap-3">
                      <span className="text-4xl font-bold text-slate-900">{s?.finalScore ?? 0}%</span>
                      <span className={`text-sm font-medium px-2 py-0.5 rounded ${riskConf.bg} ${riskConf.text}`}>
                        {s?.band || 'N/A'}
                      </span>
                      {i === 1 && s1 && s2 && <DeltaIndicator current={s2.finalScore} previous={s1.finalScore} />}
                    </div>
                    <ScoreBar score={s?.finalScore ?? 0} color={i === 0 ? 'bg-blue-500' : 'bg-emerald-500'} />
                    <div className={`p-3 rounded-lg border ${riskConf.bg} ${riskConf.border}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className={`h-4 w-4 ${riskConf.text}`} />
                        <span className={`text-sm font-semibold ${riskConf.text}`}>
                          {s?.auditRisk || 'N/A'} Audit Risk
                        </span>
                      </div>
                      {s?.reasons?.length > 0 && (
                        <ul className="text-xs text-slate-600 mt-1 space-y-0.5">
                          {s.reasons.slice(0, 3).map((r, idx) => (
                            <li key={idx} className="flex items-start gap-1">
                              <span className="mt-1 shrink-0 w-1 h-1 rounded-full bg-slate-400" />
                              {r}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Group scores (CES, PGS, CMS) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Score Components</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(SECTION_GROUPS).map(([key, group]) => (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">
                      {key} <span className="text-slate-400 font-normal">({Math.round(group.weight * 100)}% weight)</span>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {[s1, s2].map((s, i) => {
                      const val = Math.round(s?.groupScores?.[key] ?? 0);
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-sm font-bold w-10 text-right">{val}%</span>
                          <div className="flex-1">
                            <ScoreBar score={val} color={i === 0 ? 'bg-blue-400' : 'bg-emerald-400'} />
                          </div>
                          {i === 1 && <DeltaIndicator current={val} previous={Math.round(s1?.groupScores?.[key] ?? 0)} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Domain-by-domain comparison */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Domain Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {domainSections.map(sectionId => {
                const config = SECTION_CONFIG[sectionId];
                if (!config) return null;
                const score1 = Math.round(s1?.domainScores?.[sectionId]?.percentage ?? 0);
                const score2 = Math.round(s2?.domainScores?.[sectionId]?.percentage ?? 0);
                const band1 = getDomainMaturityBand(score1);
                const band2 = getDomainMaturityBand(score2);

                return (
                  <div key={sectionId} className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <p className="text-sm font-medium text-slate-700 mb-2">{config.title}</p>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { score: score1, band: band1, color: 'bg-blue-400', idx: 0 },
                        { score: score2, band: band2, color: 'bg-emerald-400', idx: 1 },
                      ].map(({ score, band, color, idx }) => (
                        <div key={idx} className="flex items-center gap-3">
                          <span className="text-sm font-bold w-10 text-right">{score}%</span>
                          <div className="flex-1">
                            <ScoreBar score={score} color={color} />
                          </div>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {band.label}
                          </Badge>
                          {idx === 1 && <DeltaIndicator current={score2} previous={score1} />}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ComparisonView;
